import { useEffect, useRef } from 'react';
import {
	useSetTradeFormPriceBox,
	useSetTradeFormSecondaryPriceBox,
} from './useSetTradeFormInputState';
import { UIMarket, UIOrderType } from '@velocity-exchange/common';
import useDriftClientIsReady from 'src/hooks/useDriftClientIsReady';
import { BigNum, PRICE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { updatePriceBoxesWithBuffers } from 'src/utils/trade';
import { useTickSizeForMarket } from 'src/hooks/useGetTickSizeForMarket';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { usePrevious } from 'react-use';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

export const useSyncPriceBoxes = (
	tradeFormSide: 'buy' | 'sell',
	priceBoxValue: string,
	secondaryPriceBoxValue: string,
	currentOrderType: UIOrderType,
	selectedMarket: UIMarket | null
) => {
	const driftClientIsReady = useDriftClientIsReady();
	const setPriceBoxValue = useSetTradeFormPriceBox();
	const setSecondaryPriceBoxValue = useSetTradeFormSecondaryPriceBox();
	const markPrice = useMarkPrice(selectedMarket?.marketId);
	const prevOrderType = usePrevious(currentOrderType);
	const prevSelectedMarketSymbol = usePrevious(
		selectedMarket?.uiSymbols.baseAssetSymbol
	);

	const tickSize = useTickSizeForMarket(selectedMarket?.marketId);

	// Ref to track the first time the below "market switch effect" runs
	const marketSwitchEffectRanOnceRef = useRef(false);

	const priceBoxValueRef = useSyncedRef(priceBoxValue);
	const secondaryPriceBoxValueRef = useSyncedRef(secondaryPriceBoxValue);

	// Give price boxes default values if user hasn't already set them.
	useEffect(() => {
		if (!driftClientIsReady) {
			return;
		}

		if (!marketSwitchEffectRanOnceRef.current) {
			// 🚨 TODO :: MULTIPLE components are using this useTradeFormStateEngineHook. Because this effect hook will always run on the first render for any secondary components, this is being erroneously triggered HALF WAY THROUGH A TRADE (e.g. when the trade form confirmation window opens, the price box is being reset because that's what marketSwitchPriceBoxUpdate does). This is a band-aid fix that should be safe for a patch but we probably need to find a better solution.
			marketSwitchEffectRanOnceRef.current = true;
			return;
		}

		updatePriceBoxesWithBuffers(
			markPrice,
			currentOrderType,
			prevOrderType,
			tradeFormSide === 'buy' ? 'long' : 'short',
			priceBoxValueRef.current,
			secondaryPriceBoxValueRef.current,
			setPriceBoxValue,
			setSecondaryPriceBoxValue,
			tickSize ? BigNum.from(tickSize, PRICE_PRECISION_EXP) : undefined,
			selectedMarket?.uiSymbols.baseAssetSymbol,
			prevSelectedMarketSymbol
		);
	}, [
		selectedMarket,
		prevSelectedMarketSymbol,
		driftClientIsReady,
		currentOrderType,
		prevOrderType,
		tradeFormSide,
		markPrice?.eqZero(),
		// tickSize?.toNumber(), 🚨 TODO Related to the TODO just above. The first time this hook runs currentPerpMarket is undefined and then has a value straight after. That causes this effect hook to tick twice quickly.
	]);
};
